import { useParams, Link } from "react-router-dom";
import { sleepApneaProducts } from "./SleepApnea";

const SleepApneaDetail = () => {
  const { id } = useParams();
  const product = sleepApneaProducts[Number(id)];

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold text-gray-900">
          Product Not Found
        </h1>

        <Link
          to="/medical-devices/sleep-apnea"
          className="inline-block mt-6 bg-blue-600 text-white px-6 py-2 rounded-md"
        >
          Back to Sleep Apnea
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">

      {/* Breadcrumb */}
      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500 mb-8">
        <Link to="/" className="hover:text-blue-600">
          Home
        </Link>

        <span>/</span>

        <Link
          to="/medical-devices/sleep-apnea"
          className="hover:text-blue-600"
        >
          Sleep Apnea
        </Link>

        <span>/</span>

        <span className="text-gray-900">
          {product.name}
        </span>
      </div>

      {/* MAIN PRODUCT */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

        {/* IMAGE */}
        <div className="border border-gray-200 rounded-xl bg-white p-8">
          <div className="w-full h-[500px] flex items-center justify-center">
            <img
              src={product.image}
              alt={product.name}
              className="max-w-full max-h-full object-contain"
            />
          </div>
        </div>

        {/* BASIC DETAILS */}
        <div>

          <p className="text-sm text-gray-500 mb-2">
            {product.brand}
          </p>

          <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
            {product.name}
          </h1>

          {product.discount && (
            <span className="inline-block mt-5 bg-green-100 text-green-700 px-3 py-1 rounded-md text-sm font-medium">
              {product.discount}
            </span>
          )}

          <div className="flex flex-wrap items-center gap-3 mt-6">
            <span className="text-3xl font-bold text-gray-900">
              {product.price}
            </span>

            {product.originalPrice && (
              <span className="text-lg text-gray-400 line-through">
                {product.originalPrice}
              </span>
            )}
          </div>

          <div className="border-t border-gray-200 my-7" />

          <h2 className="text-xl font-bold text-gray-900 mb-3">
            Product Description
          </h2>

          <p className="text-gray-600 leading-8">
            {product.description}
          </p>

          {/* ACTION BUTTONS */}
          <div className="flex flex-col sm:flex-row gap-4 mt-8">

            <button
              type="button"
              className="flex-1 bg-blue-600 text-white py-3 rounded-md font-medium hover:bg-blue-700 transition"
            >
              Add to Cart
            </button>

            <button
              type="button"
              className="flex-1 border border-blue-600 text-blue-600 py-3 rounded-md font-medium hover:bg-blue-50 transition"
            >
              Buy Now
            </button>

          </div>

        </div>
      </div>

      {/* FEATURES */}
      {product.features?.length > 0 && (
        <section className="mt-14">
          <h2 className="text-2xl font-bold text-gray-900 mb-5">
            Key Features
          </h2>

          <div className="bg-white border rounded-xl p-6">
            <ul className="list-disc pl-6 space-y-3 text-gray-600 leading-7">
              {product.features.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* SPECIFICATIONS */}
      {product.specifications &&
        Object.keys(product.specifications).length > 0 && (
          <section className="mt-14">
            <h2 className="text-2xl font-bold text-gray-900 mb-5">
              Technical Specifications
            </h2>

            <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              {Object.entries(product.specifications).map(
                ([key, value], index) => (
                  <div
                    key={key}
                    className={`grid grid-cols-1 md:grid-cols-2 ${
                      index !==
                      Object.entries(product.specifications).length - 1
                        ? "border-b"
                        : ""
                    }`}
                  >
                    <div className="p-4 font-semibold bg-gray-50 text-gray-800">
                      {key}
                    </div>

                    <div className="p-4 text-gray-600">
                      {value}
                    </div>
                  </div>
                )
              )}
            </div>
          </section>
        )}

      {/* USES */}
      {product.uses?.length > 0 && (
        <section className="mt-14">
          <h2 className="text-2xl font-bold text-gray-900 mb-5">
            Uses
          </h2>

          <div className="bg-white border rounded-xl p-6">
            <ul className="list-disc pl-6 space-y-3 text-gray-600 leading-7">
              {product.uses.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* CONTENT OF BOX */}
      {product.boxContent?.length > 0 && (
        <section className="mt-14">
          <h2 className="text-2xl font-bold text-gray-900 mb-5">
            Content of Box
          </h2>

          <div className="bg-white border rounded-xl p-6">
            <ul className="list-disc pl-6 space-y-3 text-gray-600 leading-7">
              {product.boxContent.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* WARRANTY */}
      {product.warranty && (
        <section className="mt-14">
          <h2 className="text-2xl font-bold text-gray-900 mb-5">
            Warranty
          </h2>

          <div className="bg-white border rounded-xl p-6 text-gray-600">
            {product.warranty}
          </div>
        </section>
      )}

      {/* COUNTRY */}
      {product.countryOfOrigin && (
        <section className="mt-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-5">
            Country of Origin
          </h2>

          <div className="bg-white border rounded-xl p-6 text-gray-600">
            {product.countryOfOrigin}
          </div>
        </section>
      )}

      {/* USAGE */}
      {product.usageInstructions && (
        <section className="mt-14">
          <h2 className="text-2xl font-bold text-gray-900 mb-5">
            Usage Instructions
          </h2>

          <div className="bg-white border rounded-xl p-6">
            {Array.isArray(product.usageInstructions) ? (
              <ol className="list-decimal pl-6 space-y-3 text-gray-600 leading-7">
                {product.usageInstructions.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ol>
            ) : (
              <p className="text-gray-600 leading-7">
                {product.usageInstructions}
              </p>
            )}
          </div>
        </section>
      )}

      {/* REFERENCE */}
      {product.referenceUrl && (
        <section className="mt-14">
          <a
            href={product.referenceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-gray-900 text-white px-6 py-3 rounded-md hover:bg-gray-800 transition"
          >
            View Reference Product
          </a>
        </section>
      )}

      {/* BACK */}
      <Link
        to="/medical-devices/sleep-apnea"
        className="inline-block mt-10 text-blue-600 hover:underline"
      >
        ← Back to Sleep Apnea
      </Link>

    </div>
  );
};

export default SleepApneaDetail;